import { isNil } from 'lodash'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { type GridPaginationModel } from '@mui/x-data-grid'

const DEFAULT_LOCATION_WINNERS_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: 25,
}

const getPageScrollContainer = () =>
  globalThis.document.getElementById('page-scroll-container')

const getStoredNumber = (storageKey: string): number | null => {
  const value = globalThis.sessionStorage.getItem(storageKey)
  if (isNil(value)) {
    return null
  }

  const parsedValue = Number(value)
  return Number.isNaN(parsedValue) ? null : parsedValue
}

const setStoredNumber = (storageKey: string, value: number) => {
  globalThis.sessionStorage.setItem(storageKey, String(value))
}

const isValidGridPaginationModel = (
  model: unknown
): model is GridPaginationModel => {
  if (isNil(model) || typeof model !== 'object') {
    return false
  }

  const paginationModel = model as {
    page?: unknown
    pageSize?: unknown
  }

  return (
    typeof paginationModel.page === 'number' &&
    typeof paginationModel.pageSize === 'number'
  )
}

const getStoredPaginationModel = (storageKey: string): GridPaginationModel => {
  const savedPaginationModel = globalThis.sessionStorage.getItem(storageKey)
  if (isNil(savedPaginationModel)) {
    return DEFAULT_LOCATION_WINNERS_PAGINATION_MODEL
  }

  try {
    const parsedModel = JSON.parse(savedPaginationModel)
    if (isValidGridPaginationModel(parsedModel)) {
      return parsedModel
    }
  } catch {
    // Use defaults when storage content is invalid.
  }

  return DEFAULT_LOCATION_WINNERS_PAGINATION_MODEL
}

interface UseWinnersPagePersistenceArgs {
  pathname: string
}

export const useWinnersPagePersistence = ({
  pathname,
}: UseWinnersPagePersistenceArgs) => {
  const scrollStorageKey = useMemo(
    () => `sweepstakes-winners-scroll:${pathname}`,
    [pathname]
  )
  const locationWinnersPaginationStorageKey = useMemo(
    () => `sweepstakes-winners-location-pagination:${pathname}`,
    [pathname]
  )

  const [locationWinnersPaginationModel, setLocationWinnersPaginationModel] =
    useState<GridPaginationModel>(() =>
      getStoredPaginationModel(locationWinnersPaginationStorageKey)
    )

  useEffect(() => {
    setLocationWinnersPaginationModel(
      getStoredPaginationModel(locationWinnersPaginationStorageKey)
    )
  }, [locationWinnersPaginationStorageKey])

  const saveLocationWinnersPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      globalThis.sessionStorage.setItem(
        locationWinnersPaginationStorageKey,
        JSON.stringify(model)
      )
    },
    [locationWinnersPaginationStorageKey]
  )

  const setAndPersistLocationWinnersPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      setLocationWinnersPaginationModel(model)
      saveLocationWinnersPaginationModel(model)
    },
    [saveLocationWinnersPaginationModel]
  )

  const getCurrentScrollTop = useCallback(() => {
    const scrollContainer = getPageScrollContainer()
    return scrollContainer?.scrollTop ?? window.scrollY
  }, [])

  const saveScrollPosition = useCallback(
    (explicitScrollY?: number) => {
      const scrollY = explicitScrollY ?? getCurrentScrollTop()
      setStoredNumber(scrollStorageKey, scrollY)
    },
    [getCurrentScrollTop, scrollStorageKey]
  )

  const saveCurrentScrollPosition = useCallback(() => {
    saveScrollPosition(getCurrentScrollTop())
  }, [getCurrentScrollTop, saveScrollPosition])

  const restoreScrollPosition = useCallback(() => {
    const savedScrollPosition = getStoredNumber(scrollStorageKey)

    if (savedScrollPosition !== null) {
      globalThis.requestAnimationFrame(() => {
        const scrollContainer = getPageScrollContainer()
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: savedScrollPosition,
            behavior: 'auto',
          })
          return
        }

        window.scrollTo({ top: savedScrollPosition, behavior: 'auto' })
      })
    }
  }, [scrollStorageKey])

  useEffect(() => {
    const scrollContainer = getPageScrollContainer()
    const scrollTarget = scrollContainer ?? globalThis

    const handleScroll = () => saveScrollPosition()

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
    }
  }, [saveScrollPosition])

  useLayoutEffect(() => {
    restoreScrollPosition()
    globalThis.setTimeout(() => {
      restoreScrollPosition()
    }, 0)
  }, [restoreScrollPosition, locationWinnersPaginationStorageKey])

  return {
    locationWinnersPaginationModel,
    setAndPersistLocationWinnersPaginationModel,
    saveCurrentScrollPosition,
  }
}
